"use client";

import DividerWithText from "@/components/atoms/dividerWithText/DividerWithText";
import GoogleIcon from "@/components/atoms/svg/icons/GoogleIcon";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { getApps, initializeApp } from "@firebase/app";
import { getAuth, GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { useToast } from "@/components/ui/toastercustom";
import useLoginWithProvider from "@/features/authentication/hooks/useLoginWithProvider";
import DiscordIcon from "@/components/atoms/svg/social/DiscordIcon";
import { useOrigin } from "@/features/authentication/hooks/useOrigin";
import { IConnectSocial } from "../../interfaces/IAuth";

interface ILoginThirdPartyProps {
  pageType?: "login" | "register";
}

export default function LoginThirdParty({
  pageType = "login",
}: ILoginThirdPartyProps) {
  const { toast } = useToast();
  const { mutateLoginWithProvider, signInWithDiscord } = useLoginWithProvider();
  const urlOrigin = useOrigin();

  const getTextByType = () => {
    if (pageType === "login") {
      return "Don't have an account?";
    } else if (pageType === "register") {
      return "Already have an account?";
    }
    return "";
  };

  const firebaseConfig = {
    apiKey: process.env.NEXT_PUBLIC_FIREBASE_APIKEY,
    authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTHDOMAIN,
    projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_Id,
    storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SEND_ID,
    appId: process.env.NEXT_PUBLIC_FIREBASE_APPID,
    measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
  };

  if (!getApps().length) {
    initializeApp(firebaseConfig);
  }

  const auth = getAuth();

  const getButtonByType = () => {
    if (pageType === "login") {
      return <Link href={"/register"}>Sign up</Link>;
    } else if (pageType === "register") {
      return <Link href={"/login"}>Log in</Link>;
    }
    return null;
  };

  const googleLogin = async () => {
    const provider = new GoogleAuthProvider();
    provider.addScope("email");
    await signInWithPopup(auth, provider)
      .then((result) => {
        const { user } = result;
        if (
          user.providerData[0].email !== null &&
          user.providerData[0].email !== undefined &&
          result.providerId !== null &&
          result.providerId !== undefined
        ) {
          mutateLoginWithProvider({
            email: user.providerData[0].email,
            provider: "google",
            providerUUID: user.uid,
          });
        } else {
          toast({
            title: "Loged In failed",
            type: "error",
            text: "Logged unsuccessfully",
          });
        }
      })
      .catch((_error) => {
        toast({
          title: "Error",
          type: "error",
          text: _error.message || "Failed to Login",
        });
      });
  };

  return (
    <div className="flex justify-center items-center flex-col font-[500] my-6">
      <DividerWithText
        content={
          <span className="whitespace-nowrap inline-block relative top-[-2px] text-[12px]">
            Or authorize with
          </span>
        }
      />
      <div className="flex gap-2 w-full my-6">
        <Button
          className="w-full btn-default flex-1 gap-1"
          onClick={googleLogin}
        >
          <GoogleIcon />
          <span>Google</span>
        </Button>
        <Button
          className="w-full btn-default flex-1 gap-1"
          onClick={() =>
            signInWithDiscord({
              url: urlOrigin,
              route: IConnectSocial.DISCORD,
            })
          }
        >
          <DiscordIcon color="#0F132499" />
          <span>Discord</span>
        </Button>
      </div>
      <div className="flex flex-col gap-2 justify-center items-center w-full">
        {pageType === "login" && (
          <Link className="forgot-password" href={"/forgot-password"}>
            Forgot password?
          </Link>
        )}
        <div className="text-account">
          <span className="have-account mr-1">{getTextByType()}</span>
          {getButtonByType()}
        </div>
      </div>
    </div>
  );
}
